import { useEffect, useMemo, useState } from "react";
import {
  Search, MapPin, ChevronDown, Building2, Factory, GraduationCap,
  Landmark, Server, ShoppingBag, Home as HomeIcon, Zap, Hospital,
} from "lucide-react";
import Reveal from "../components/Reveal";
import { PageHero } from "../components/Shared";
import { fetchProjectsSupabase } from "../lib/supabase";
import SEO from "../components/SEO";

function sectorIcon(sector = "") {
  const s = sector.toLowerCase();
  if (s.includes("industrial")) return Factory;
  if (s.includes("educat")) return GraduationCap;
  if (s.includes("govern")) return Landmark;
  if (s.includes("it") || s.includes("data")) return Server;
  if (s.includes("showroom") || s.includes("mall") || s.includes("shopping")) return ShoppingBag;
  if (s.includes("resident") || s.includes("apart")) return HomeIcon;
  if (s.includes("commercial")) return Building2;
  if (s.includes("hospital") || s.includes("biotech")) return Hospital;
  return Zap;
}

export default function Projects() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("All");
  const [openSector, setOpenSector] = useState(null);

  useEffect(() => {
    fetchProjectsSupabase()
      .then(setAll)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const current = useMemo(() => all.filter((p) => p.group === "current" && p.status === "completed"), [all]);
  const legacy = useMemo(() => all.filter((p) => p.group === "legacy"), [all]);

  const sectors = useMemo(() => {
    const s = [...new Set(current.map((p) => p.sector).filter(Boolean))].sort();
    return ["All", ...s];
  }, [current]);

  const filtered = useMemo(() => {
    let list = current;
    if (sector !== "All") {
      list = list.filter((p) => p.sector === sector);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.location || "").toLowerCase().includes(q) ||
          (p.sector || "").toLowerCase().includes(q) ||
          (p.client || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [current, query, sector]);

  const legacyBySector = useMemo(() => {
    const map = {};
    legacy.forEach((p) => {
      (map[p.sector] = map[p.sector] || []).push(p);
    });
    return map;
  }, [legacy]);

  return (
    <>
      <SEO
        title="Projects | SUV Engineers"
        description="Explore our portfolio of completed and ongoing electrical installations, HT/LT services, and networking projects across India."
      />
      <PageHero crumb="Projects" title="Selected electrical &amp; networking work.">
        A snapshot of our ongoing sites and completed projects under SUV Engineers — backed by a deep legacy
        of work delivered by us across every major sector.
      </PageHero>

      {/* CURRENT PROJECTS */}
      <section className="section muted">
        <div className="section-inner">
          <div className="proj-filters">
            <div className="sector-pills">
              {sectors.map((s) => (
                <button
                  key={s}
                  className={`sector-pill${sector === s ? " active" : ""}`}
                  onClick={() => setSector(s)}
                  data-testid={`sector-filter-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="search-box" style={{ margin: 0, width: "100%", maxWidth: "360px" }}>
              <Search />
              <input
                placeholder="Search projects, locations, clients…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="project-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">No projects match your search.</div>
          ) : (
            <>
              <div className="proj-summary" data-testid="project-summary">
                Showing <b>{filtered.length}</b> completed projects
                {query.trim() && <> for “<b>{query}</b>”</>}
              </div>
              <div className="proj-list" data-testid="project-grid">
                {filtered.map((p, i) => {
                  const Icon = sectorIcon(p.sector);
                  return (
                    <Reveal key={p.id} className={`proj-row ${p.status}`} delay={(i % 10) * 30}>
                      <span className="proj-ic"><Icon /></span>
                      <div className="proj-info">
                        <div className="pn">{p.name}</div>
                        <div className="pm">
                          <span className="sec">{p.sector}</span>
                          {p.location && <span className="loc"><MapPin /> {p.location}</span>}
                        </div>
                      </div>
                      <span className={`proj-dot ${p.status}`}>
                        <span className="d" />{p.status === "ongoing" ? "Ongoing" : "Completed"}
                      </span>
                    </Reveal>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* LEGACY */}
      {legacy.length > 0 && (
        <section className="section">
          <div className="section-inner">
            <Reveal className="section-head">
              <p className="eyebrow">Legacy Track Record</p>
              <h2>Projects executed by us.</h2>
              <p>
                Decades of work delivered under our promoters' earlier companies, organised by sector.
                Tap a sector to explore the clients.
              </p>
            </Reveal>
            <div data-testid="legacy-list">
              {Object.entries(legacyBySector).map(([sector, items]) => {
                const open = openSector === sector;
                return (
                  <div key={sector} className={`legacy-sector ${open ? "open" : ""}`}>
                    <button
                      className="legacy-head"
                      onClick={() => setOpenSector(open ? null : sector)}
                      data-testid={`legacy-sector-${sector}`}
                    >
                      <span className="left">
                        {sector}
                        <span className="badge">{items.length}</span>
                      </span>
                      <ChevronDown />
                    </button>
                    <div className="legacy-body">
                      <div className="legacy-body-inner">
                        <div className="legacy-items">
                          {items.map((p) => (
                            <span key={p.id} className="legacy-chip">{p.name}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
